import React from 'react';
import Layout from '../components/site/Layout';
import { PageHero, CtaBanner, SectionHeading, Chips } from '../components/site/PageParts';
import { Arrow } from '../components/site/Button';
import { Reveal } from '../components/site/motion';
import useLang from '../i18n/useLang';

// Tints for the tool cards, keyed by group id; the group with points (GitHub) gets the wide white card.
const TONES = { python: 'bg-mint-50', databases: 'bg-sky-50', interfaces: 'bg-fog-50', ai: 'bg-mint-50' };

const lead = { key: 'DMC', url: 'https://www.sap.com/products/scm/digital-manufacturing-cloud.html' };

const others = [
  { key: 'ME', url: 'https://www.sap.com/products/scm/execution-mes.html', tone: 'bg-sky-50' },
  { key: 'MII', url: 'https://www.sap.com/products/scm/manufacturing-intelligence-integration.html', tone: 'bg-fog-50' },
  { key: 'PCO', tone: 'bg-mint-50' },
  { key: 'BTP', tone: 'bg-fog-50' },
  { key: 'Fiori', url: 'https://www.sap.com/products/technology-platform/fiori.html', tone: 'bg-mint-50' },
  { key: 'UI5', tone: 'bg-sky-50' },
  { key: 'AI', tone: 'bg-sky-50' },
  { key: 'CustomComplexDevelopments', tone: 'bg-fog-50' },
];

function SapLink({ url, label }) {
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="group mt-6 inline-flex items-center gap-1.5 text-[0.95rem] font-medium text-grovv-800 hover:text-ink">
      {label}
      <Arrow className="h-3.5 w-3.5" />
    </a>
  );
}

// The tools I build with and the method around them: copy and tool names come from the translation files.
function Tools() {
  const { t } = useLang();
  const raw = t('pages.technology.groups', { returnObjects: true });
  const groups = Array.isArray(raw) ? raw : [];
  const hasPoints = (g) => Array.isArray(g.points) && g.points.length > 0;
  const wide = groups.filter(hasPoints);
  const cards = groups.filter((g) => !hasPoints(g));
  if (!groups.length) return null;

  return (
    <section className="wrap pt-20 sm:pt-24">
      {wide.map((g) => (
        <Reveal as="article" key={g.id} className="mb-5 rounded-4xl bg-white p-8 shadow-card ring-1 ring-fog-200 sm:p-10">
          <h2 className="heading text-2xl text-ink sm:text-3xl">{g.title}</h2>
          <p className="mt-4 max-w-[60ch] text-[1.05rem] leading-[1.75] text-ink-600">{g.body}</p>
          <ul className="mt-8 grid grid-cols-1 gap-x-10 gap-y-6 md:grid-cols-2">
            {g.points.map((pt) => (
              <li key={pt.title} className="border-t border-fog-200 pt-5">
                <h3 className="text-lg font-semibold text-ink">{pt.title}</h3>
                <p className="mt-2 text-[1.05rem] leading-relaxed text-ink-600">{pt.body}</p>
              </li>
            ))}
          </ul>
          <Chips items={g.stack} className="mt-8" />
        </Reveal>
      ))}

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        {cards.map((g, i) => (
          <Reveal as="article" key={g.id} delay={(i % 2) * 90} className={`flex flex-col rounded-4xl p-7 sm:p-9 ${TONES[g.id] || 'bg-fog-50'}`}>
            <h2 className="heading text-2xl text-ink sm:text-3xl">{g.title}</h2>
            <p className="mt-4 text-[1.05rem] leading-[1.75] text-ink-600">{g.body}</p>
            <div className="mt-auto">
              <Chips items={g.stack} onTint />
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export default function Competence() {
  const { t } = useLang();
  const h = (k) => t(`Competence.${k}_H`);
  const c = (k) => t(`Competence.${k}_C`);
  const linkLabel = t('pages.technology.sap');

  return (
    <Layout title={t('header.Competence')}>
      <PageHero tone="sky" title={t('pages.technology.title')} lead={t('pages.technology.lead')} />

      <Tools />

      <section className="wrap py-20 sm:py-24">
        <Reveal>
          <SectionHeading title={t('pages.technology.sapTitle')} lead={t('pages.technology.sapLead')} />
        </Reveal>

        <article className="relative isolate mt-12 grid grid-cols-1 gap-10 overflow-hidden rounded-[2.5rem] bg-navy-900 p-8 text-white sm:p-12 lg:grid-cols-[1fr_1.3fr] lg:items-center">
          <div aria-hidden="true" className="absolute -bottom-32 -start-24 -z-10 h-96 w-96 rounded-full bg-grovv/20 blur-3xl" />
          <h3 dir="ltr" className="display text-start text-5xl sm:text-7xl rtl:text-end">
            {h(lead.key)}
          </h3>
          <div>
            <p className="text-lg leading-[1.75] text-navy-200">{c(lead.key)}</p>
            <a
              href={lead.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group mt-6 inline-flex items-center gap-1.5 text-[0.95rem] font-medium text-grovv-300 hover:text-white"
            >
              {linkLabel}
              <Arrow className="h-3.5 w-3.5" />
            </a>
          </div>
        </article>

        <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
          {others.map((item) => (
            <article key={item.key} className={`flex flex-col rounded-4xl p-7 sm:p-9 ${item.tone}`}>
              <h3 className="heading text-2xl text-ink sm:text-3xl">{h(item.key)}</h3>
              <p className="mt-4 text-[1.05rem] leading-[1.75] text-ink-600">{c(item.key)}</p>
              <SapLink url={item.url} label={linkLabel} />
            </article>
          ))}
        </div>
      </section>

      <CtaBanner />
    </Layout>
  );
}
